import { computed, shallowReactive, type ComputedRef } from 'vue'

import { OlympusClient } from '@/OlympusClient'

import { type HostedVideoFailure } from './hostedVideoErrors'

export interface HostedVideoValue {
  mp4: string
  webm: string
  thumbnail: string
}

interface HostedVideoPollingHandlers {
  onFailure: (error: unknown, fallback: HostedVideoFailure) => void
  onVideoReady: (value: HostedVideoValue) => Promise<void> | void
}

interface HostedVideoPollingTask extends HostedVideoPollingHandlers {
  attemptId: number
  brandId: string | null
  completion: Promise<void> | null
  mediaId: string | null
  timer: number | null
  uploadKey: string
}

export interface HostedVideoUploadToken {
  attemptId: number
  ready: Promise<void>
  uploadKey: string
}

const POLL_INTERVAL_MS = 3000
const activeUploads = shallowReactive(new Map<string, HostedVideoPollingTask>())
let nextAttemptId = 0

const isCurrentTask = (task: HostedVideoPollingTask): boolean =>
  activeUploads.get(task.uploadKey) === task

const removeTask = (task: HostedVideoPollingTask): void => {
  if (!isCurrentTask(task)) return
  if (task.timer !== null) window.clearTimeout(task.timer)
  activeUploads.delete(task.uploadKey)
}

const cancelTask = async (task: HostedVideoPollingTask): Promise<void> => {
  const completion = task.completion ?? Promise.resolve()
  removeTask(task)
  return await completion.catch(() => undefined)
}

const failTask = (
  task: HostedVideoPollingTask,
  error: unknown,
  fallback: HostedVideoFailure
): void => {
  removeTask(task)
  task.onFailure(error, fallback)
}

const pollTask = async (task: HostedVideoPollingTask): Promise<void> => {
  if (!task.brandId || !task.mediaId) return

  let result
  try {
    result =
      await OlympusClient.growth.v2.websiteBuilder.brand.video.getVideoStatus({
        params: { brandId: task.brandId, mediaId: task.mediaId },
      })
  } catch (error) {
    if (isCurrentTask(task)) failTask(task, error, 'status')
    return
  }

  if (!isCurrentTask(task)) return

  if (result.status === 'failed' || result.status === 'archived') {
    failTask(task, new Error(`Video conversion ${result.status}`), 'failed')
    return
  }

  if (
    result.status === 'ready' &&
    result.mp4 &&
    result.webm &&
    result.thumbnail
  ) {
    try {
      task.completion = Promise.resolve(
        task.onVideoReady({
          mp4: result.mp4,
          thumbnail: result.thumbnail,
          webm: result.webm,
        })
      )
      await task.completion
      removeTask(task)
    } catch (error) {
      if (isCurrentTask(task)) failTask(task, error, 'save')
    }
    return
  }

  if (result.status === 'ready') {
    failTask(task, new Error('Video delivery files are missing'), 'missing')
    return
  }

  task.timer = window.setTimeout(() => {
    task.timer = null
    void pollTask(task)
  }, POLL_INTERVAL_MS)
}

export const beginHostedVideoUpload = (
  uploadKey: string,
  handlers: HostedVideoPollingHandlers
): HostedVideoUploadToken => {
  const currentTask = activeUploads.get(uploadKey)
  const ready = currentTask ? cancelTask(currentTask) : Promise.resolve()
  const attemptId = ++nextAttemptId
  const task: HostedVideoPollingTask = {
    ...handlers,
    attemptId,
    brandId: null,
    completion: ready,
    mediaId: null,
    timer: null,
    uploadKey,
  }
  activeUploads.set(uploadKey, task)
  return { attemptId, ready, uploadKey }
}

export const isHostedVideoUploadCurrent = (
  token: HostedVideoUploadToken
): boolean => activeUploads.get(token.uploadKey)?.attemptId === token.attemptId

export const startHostedVideoPolling = (
  token: HostedVideoUploadToken,
  brandId: string,
  mediaId: string,
  handlers: HostedVideoPollingHandlers
): void => {
  const task = activeUploads.get(token.uploadKey)
  if (!task || task.attemptId !== token.attemptId) return

  task.brandId = brandId
  task.mediaId = mediaId
  task.onFailure = handlers.onFailure
  task.onVideoReady = handlers.onVideoReady
  void pollTask(task)
}

export const updateHostedVideoPollingHandlers = (
  uploadKey: string,
  handlers: HostedVideoPollingHandlers
): void => {
  const task = activeUploads.get(uploadKey)
  if (!task) return
  task.onFailure = handlers.onFailure
  task.onVideoReady = handlers.onVideoReady
}

export const stopHostedVideoPolling = async (
  uploadKey: string
): Promise<void> => {
  const task = activeUploads.get(uploadKey)
  if (task) await cancelTask(task)
}

export const useHostedVideoPolling = (
  uploadKey: string
): ComputedRef<boolean> => computed(() => activeUploads.has(uploadKey))
